// Constants
import { AnalyticsConstants, AutoPayType, CancelReasonEnum, DisbursementTypeEnum, DisclosureType, ErrorTypes, ModalIdentifier, PaymentTypeEnum, TlxeLoanStatusEnum } from '../../constants'

// Libraries
import moment from 'moment'

// Services
import api from '../../api'
import router from '../../router'
import store from '../../store'

// Utilities
import Analytics from '../../utility/analyticsUtil'
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'
import Logger from '../../utility/loggerUtil'
import Session from '../../utility/sessionUtil'

// Validators
import { phone } from '../../validators/phone'

export default {
  async submitPayment (context, data = {}) {
    if (context.state.pending) {
      return
    }

    context.commit('paymentPending')

    try {
      const paymentObject = preparePaymentObject(context, data, true)
      const payment = await api.payment.postPayment(paymentObject)

      Logger.info('Sent payment info successfully')
      Logger.debug(payment)
      context.commit('setPaymentData', { paymentKey: payment.messageKey })
    } catch (e) {
      let serviceCallErrorText
      let serviceCallError

      if (e.response?.data?.Error?.includes(ErrorTypes.profileLocked)) {
        const errorMessage = `${e.response.data.Error}`
        Session.setNetworkErrorCookie(errorMessage, true)
        await context.dispatch('clearRemoteState')
        Analytics.logEvent(AnalyticsConstants.eventName.logout, { method: AnalyticsConstants.method.logout.profileLock }, { logMarketingEvent: true })
        Analytics.logEvent(AnalyticsConstants.eventName.completeCheckout, { method: AnalyticsConstants.other.cancelCheckoutType, item_id: context.state.loan.loanId }, { logMarketingEvent: true })

        return ErrorTypes.profileLocked
      } else if (e.response?.data?.Error?.length > 0) {
        serviceCallErrorText = typeof e.response.data.Error === 'string' ? e.response.data.Error : e.response.data.Error.join('\n\n')
        serviceCallError = { Error: serviceCallErrorText, disablePayButton: true }
      } else if (e.message.includes('status code 502') || (e.response?.status?.length > 0 && e.response?.status === 502)) {
        serviceCallErrorText = `${Language.$t('global.server_error')}: ${Language.$t('global.network_error')}`
        serviceCallError = { Error: serviceCallErrorText, disablePayButton: false }
      } else {
        serviceCallErrorText = `${Language.$t('global.server_error')}: ${e.message}.`
        serviceCallError = { Error: serviceCallErrorText, disablePayButton: false }
      }

      context.commit('setPaymentResult', serviceCallError)
      context.commit('paymentComplete')
    }
  },

  /**
   * Continuously check the payment status from the API until we get either an error or status update.
   */
  async checkPaymentStatus (context) {
    setTimeout(async function () {
      if (context.state.pending) {
        try {
          const confirmation = await api.payment.getPaymentStatus(context.state.paymentKey)
          Logger.info('Got payment confirmation response')
          Logger.debug(confirmation)
          if (!isEmpty(confirmation) && isEmpty(confirmation.Error)) {
            if (context.state.guestCard && !context.state.saveGuestCard) { // If guest card was used and we don't want to save it, delete it
              const card = store.getters['cards/getCardById'](context.state.paymentCardIds)

              if (card !== undefined) {
                try {
                  await store.dispatch('cards/removePayDebitCard', card).catch(e => {
                    Logger.warn('Error removing card, trying again')
                    Logger.debug(e)

                    return store.dispatch('cards/removePayDebitCard', card)
                  })

                  context.commit('setPaymentData', { guestCard: false }) // Clearing guest card
                } catch (e) {
                  Logger.error('Error removing card')
                  Logger.debug(e) // Ignore error and try again on confirm payment
                }
              }
            }

            context.commit('setPaymentResult', confirmation)
            context.commit('paymentComplete')

            context.dispatch('setAlternatePartyLockout', {
              isLocked: true,
              loanId: confirmation.newLoanId
            })
          } else if (!isEmpty(confirmation.Error)) {
            const confirmError = typeof confirmation.Error === 'string' ? confirmation.Error : confirmation.Error.join('\n\n')
            if (confirmError.includes(ErrorTypes.profileLocked)) {
              Session.setNetworkErrorCookie(confirmError, true)
              Analytics.logEvent(AnalyticsConstants.eventName.completeCheckout, { method: AnalyticsConstants.other.cancelCheckoutType, item_id: context.state.loan.loanId }, { logMarketingEvent: true })

              throw new Error(ErrorTypes.profileLocked, { cause: 'PaymentResponse' })
            }

            throw new Error(confirmError, { cause: 'PaymentResponse' })
          } else {
            context.dispatch('checkPaymentStatus') // call this action again if need be
          }
        } catch (e) {
          if (e.cause === 'PaymentResponse') {
            const serviceCallError = { Error: e.message, disablePayButton: true }
            context.commit('setPaymentResult', serviceCallError)
            context.commit('paymentComplete')
          } else {
            context.dispatch('checkPaymentStatus') // Ignore errors from server and call this action again
          }
        }
      } else {
        context.commit('paymentComplete')
      }
    }, 1000)
  },

  async updateRemoteState (context) {
    try {
      const currentRoute = router.currentRoute.fullPath
      await api.appState.postPaymentState(context.state.loan.loanId, {
        state: context.state,
        route: currentRoute
      }).catch(e => {
        Logger.error('Error updating state on server')
        Logger.debug(e)

        return api.appState.postPaymentState(context.state.loan.loanId, {
          state: context.state,
          route: currentRoute
        })
      })
    } catch (e) {
      Logger.error('Error updating remote state')
      Logger.debug(e)
    }
  },

  async clearPaymentData (context) {
    try {
      // Clear vue payment store
      context.commit('clearPaymentData')

      // Clear server payment store
      await context.dispatch('clearRemoteState')
    } catch (e) {
      Logger.error('Error clearing payment data')
      Logger.debug(e)
    }
  },

  async clearRemoteState () {
    try {
      await api.appState.deletePaymentState()
    } catch (e) {
      Logger.error('Error clearing remote state')
      Logger.debug(e)
    }
  },

  async setAlternatePartyLockout (context, data) {
    Logger.info(`Setting alternate party lockout to isLocked: ${data.isLocked}`)

    if (!context.rootGetters['payment/isRefiFlowEligible'] || context.getters.isSettlementPayment) {
      return true
    }

    const cacheValue = {
      profileId: context.rootGetters['session/getCognitoId'],
      customerId: context.rootState.customer.customerId,
      delete: data.isLocked === false
    }

    const postState = async function (loanId) {
      try {
        const result = await api.altParty.setLoanRefiAltPartyLockout(loanId, cacheValue)

        Logger.debug('Refi alternate party result check complete')
        return result.alreadyLocked === undefined || result.alreadyLocked === false
      } catch (e) {
        Logger.error(`Error updating alternate party lockout state for Loan ID: ${loanId} (attempt 1)`)

        try {
          const result2 = await api.altParty.setLoanRefiAltPartyLockout(loanId, cacheValue)
          return result2.alreadyLocked === undefined || result2.alreadyLocked === false
        } catch (e2) {
          Logger.error(`Error updating alternate party lockout state for Loan ID: ${loanId} (attempt 2)`)
          throw e2
        }
      }
    }

    if (!isEmpty(data.loanId)) {
      return postState(data.loanId)
    }

    const oldLoanResult = await postState(context.rootState.payment.loan.loanId)

    if (oldLoanResult === false) {
      router.push({ name: 'AlternateBlocked', params: { loanId: context.rootState.payment.loan.loanId, isLocked: true, alternateName: oldLoanResult.lockedByName } })

      Logger.debug('Checking refi alternate party for old loan')
      await context.dispatch('clearPaymentData') // Clear remote state on cancel
      return false
    }

    if (!isEmpty(context.rootState.payment.result) && !isEmpty(context.rootState.payment.result.newLoanId)) {
      const newLoanResult = await postState(context.rootState.payment.result.newLoanId)

      if (newLoanResult === false) {
        router.push({ name: 'AlternateBlocked', params: { loanId: context.rootState.payment.result.newLoanId, isLocked: true, alternateName: oldLoanResult.lockedByName } })

        Logger.debug('Checking refi alternate party for new loan')
        await context.dispatch('clearPaymentData') // Clear remote state on cancel
        return false
      }
    }

    return true
  },

  /**
   * Cancels the refinance after the payment has already been made.
   * @param {Object} context - The Vuex store context object.
   * @param {Object} data - The config data.
   * @remarks - Calls an API to queue a Lambda request (so it instantly returns) and polls another API for the response.
   */
  async cancelRefinance (context, data = {}) {
    let username = context.rootGetters['session/getUsername']
    const isPhone = phone(username)

    if (!isEmpty(username) && isPhone) {
      username = username.substring(0, 3) + '-' + username.substring(3, 6) + '-' + username.substring(6)
    }

    const newLoanHeader = context.state.result.newLoanHeaderId
    const confirmationNumber = isEmpty(data.confirmationNumber) ? context.state.result.confirmationNumber : data.confirmationNumber
    const applicationId = data.applicationId

    const confirmationObj = {
      profileId: context.rootGetters['session/getCognitoId'],
      customerId: context.state.isAlternatesTurn ? context.getters.altPartyCustomerId : context.rootState.customer.customerId,
      phoneNumber: isPhone ? username : undefined,
      email: isPhone ? undefined : username,
      cardHolderName: context.state.cardHolderName,
      loanId: context.state.loan.loanId,
      amountPaid: context.state.paymentAmount,
      cardNumber: context.state.cardNumber,
      customerName: context.rootState.customer.customerName.split(' ')[0],
      storePhoneNumber: context.state.storePhoneNumber,
      productBrand: context.state.brand,
      collateralYear: context.state.loan.collateralDetail.year,
      collateralMake: context.state.loan.collateralDetail.make,
      collateralModel: context.state.loan.collateralDetail.model,
      confirmationNumber,
      paymentDueDate: context.state.loan.dueDate,
      loanSigned: data.loanSigned === true,
      isReapply: !isEmpty(applicationId)
    }

    const response = await api.loan.cancelRenewal(newLoanHeader, confirmationObj, applicationId).catch(e => {
      Logger.warn('Issue canceling renewal, trying again')
      return api.loan.cancelRenewal(newLoanHeader, confirmationObj, applicationId)
    })

    const messageKey = response.messageKey
    const startTime = moment().unix()
    let currentTime = moment().unix()
    let success = false

    const cancelRefiTimeout = context.rootState.session.cancelRefiTimeout
    let cancelRefiStatusResponse

    while ((currentTime - startTime) < cancelRefiTimeout) {
      Logger.info('Polling for Refi Cancellation status...')
      cancelRefiStatusResponse = await api.loan.getCancelRenewalStatus(messageKey).catch(async e => {
        Logger.error('Polling for Refi Cancellation status failed. Will likely try again.')
        Logger.debug(e)
      })

      if (isEmpty(cancelRefiStatusResponse?.Error) && cancelRefiStatusResponse?.Success) {
        success = true
        break
      } else if (!isEmpty(cancelRefiStatusResponse?.Error)) {
        // quit immediately if we encounter any errors in any of our status checks
        success = false
        break
      }

      // wait until we try again
      await new Promise(resolve => {
        setTimeout(resolve, 1000)
      })

      currentTime = moment().unix()
    }

    // throwing an error will keep the places that call this action behaving as they do before the polling was introduced
    if (!success) {
      throw new Error(`Didn't receive a successful response after checking for ${cancelRefiTimeout} seconds.`)
    }
  },

  async getAncillaryProducts (context, data) {
    const loanHeader = isEmpty(data) || isEmpty(data.loanHeader) ? context.state.loan.loanHeader : data.loanHeader
    const paymentAmount = context.state.paymentAmount
    const productId = isEmpty(data) || isEmpty(data.productId) ? context.state.loan.productId : data.productId
    const storeId = isEmpty(data) || isEmpty(data.storeId) ? context.state.loan.storeId : data.storeId
    const ancillaryProductsResponse = await api.loan.getAncillaryProducts(loanHeader, paymentAmount, productId, storeId)
      .catch(e => {
        Logger.error('Error on api.loan.getAncillaryProducts')
        Logger.debug(e)
        return api.loan.getAncillaryProducts(loanHeader, paymentAmount, productId, storeId)
      })
    if (Array.isArray(ancillaryProductsResponse.products)) {
      context.commit('setPaymentData', {
        ancillaryProducts: ancillaryProductsResponse.products,
        ancillaryConfirmText: ancillaryProductsResponse.confirmText,
        ancillaryRequiredText: ancillaryProductsResponse.missingProducts,
        ancillaryShowApptLink: ancillaryProductsResponse.showAppointmentLink
      })
    }
  },

  async drawFunds (context) {
    const { lmsId, loanHeader, loanId, storeId } = context.state.loan
    const { disbursementAmount, disburseCardIds, disbursementType, guestPayment } = context.state
    let username = context.rootState.session.username

    if (phone(username.substring(2))) {
      username = username.substring(2)
    }

    return api.loan.draw(loanHeader, loanId, storeId, disbursementAmount, disburseCardIds, disbursementType, lmsId, username, guestPayment)
  },

  async getPostDrawStatus (context, data) {
    try {
      const drawStatus = await api.loan.getDrawStatus(data.messageKey)
      return drawStatus
    } catch (e) {
      Logger.error('Error getting Post Draw status.')
      Logger.debug(e)
      throw e
    }
  },

  cancelRenewal (context) {
    if (context.state.paymentAmount > 0) {
      router.push({ name: 'ConfirmPayment', params: { isCancelledRefi: true, cancelReason: CancelReasonEnum.cancelRenewal } })
    } else {
      router.push({ name: 'Dashboard' })
    }
  },

  async checkDisbursementSuccess (context) {
    const { newLoanHeaderId, newLoanProductId } = context.state.result
    const disbursementType = context.state.disbursementType
    const applicationId = context.state.isReapply ? context.state.origination.applicationId : undefined

    try {
      return await api.payment.getReferenceNumber(newLoanHeaderId, newLoanProductId, disbursementType, applicationId)
    } catch (e) {
      Logger.error(`Error checking disbursement status for: ${newLoanHeaderId} type: ${disbursementType}`)
      Logger.debug(e)
      throw e
    }
  },

  async getDemographicReferences (context, data) {
    try {
      const demoRefs = await api.payment.getDemographicReferences(data.productId, data.customerId, data.loanHeader, data.storeId)

      if (!isEmpty(demoRefs) && !isEmpty(demoRefs.demographicReference)) {
        context.commit('setDemoRequirements', demoRefs.demographicReference)
      }
    } catch (e) {
      Logger.error('Error getting demographic references.')
      Logger.debug(e)
      throw e
    }
  },

  async saveRestrictedStateAcknowledgement (context, data) {
    try {
      await api.payment.saveRestrictedStateAcknowledgement(context.state.loan.loanId, moment.utc().format(), data.didConsent, context.state.loan.lmsId, context.rootGetters['store/getStoreStateAbrev'](data.storeId), data.storeId, data.transactionType)
    } catch (e) {
      Logger.error('Error saving state acknowledgement')
      Logger.debug(e)
      throw e
    }
  },

  async saveAchAdditionalPaymentConsent (context, data) {
    try {
      await api.payment.saveAchAdditionalPaymentConsent(context.state.loan.loanId, data.userConsented)
    } catch (e) {
      Logger.error('Error saving ACH additional payment consent')
      Logger.debug(e)
      throw e
    }
  },

  async isAddOnLocked (context) {
    try {
      if (context.state.isAddOnLocked) {
        // Reset so message can be cleared and reappear if needed.
        context.commit('setAddOnLock', false)
      }

      const response = await api.payment.isAddOnLocked()
      const isLocked = response.isLocked
      context.commit('setAddOnLock', isLocked)

      return isLocked
    } catch (e) {
      Logger.error('Error checking is add on lock.')
      Logger.debug(e)
      context.commit('setAddOnLock', false)
      return false
    }
  },

  async tryAddOnLock (context) {
    try {
      const response = await api.payment.tryAddOnLock(context.state.loan.loanHeader, context.state.loan.storeId, context.rootState.session.gpsPosition.latitude, context.rootState.session.gpsPosition.longitude)
      const isLocked = response.isLocked
      context.commit('setAddOnLock', isLocked)

      return isLocked
    } catch (e) {
      Logger.error('Error attempting check add on lock.')
      Logger.debug(e)

      if (e.status === 400) {
        context.commit('setAddOnLock', true)
        return true
      } else {
        return false
      }
    }
  },

  async getOlpaDocument (context, data) {
    const disclosures = await api.documents.getDisclosures(
      context.state.loan.storeId,
      context.state.loan.productId,
      context.state.migrateProduct,
      DisclosureType.refinance,
      context.state.isAddon,
      context.state.isAlternatesTurn,
      data?.isAutoPay,
      context.state.cardNumber ?? 'N/A',
      context.rootGetters['customer/activeBank']?.accountNumber ?? 'N/A',
      context.rootState.customer.customerName,
      context.state.loan.loanHeader,
      context.state.loan.loanId
    )
    return disclosures.find(d => d.name?.toLowerCase()?.includes('lpaagreement'))
  },

  async postOlpaDocument (context, data) {
    try {
      await api.documents.postOlpaDisclosure(context.state.loan.loanHeader, context.state.loan.loanId, data.file)
    } catch (error) {
      Logger.error('Error attempting upload OLPA document.')
      Logger.debug(error)
      throw error
    }
  },

  /**
   * Prepares to resume a previously started refinance/addon or reapply.
   * @param {Object} context - The Vuex store context object.
   * @param {{isReapply: Boolean, pendingTransactionInfo: {newLoanHeader: String, newLoanId: String, newLoanDueDate: String}}} data - The pending refi/reapply info object.
   * @returns {String} The page to route to.
   */
  async resumeTransactionState (context, { isReapply, pendingTransactionInfo }) {
    try {
      let prevPayState = {}

      const remotePaymentState = await api.appState.getPaymentState(pendingTransactionInfo.newLoanHeader, pendingTransactionInfo.newLoanId, pendingTransactionInfo.newLoanDueDate).catch(e => {
        Logger.error('Error getting previous transaction state')
        Logger.debug(e)
        return api.appState.getPaymentState(pendingTransactionInfo.newLoanHeader, pendingTransactionInfo.newLoanId, pendingTransactionInfo.newLoanDueDate)
      })

      if (isEmpty(remotePaymentState.data.route)) {
        throw new Error('Missing Route')
      }

      if (!isEmpty(remotePaymentState)) {
        prevPayState = remotePaymentState.data.state
      }

      const lockState = await context.dispatch('setAlternatePartyLockout', {
        loanId: !isEmpty(prevPayState.loan) ? prevPayState.loan.loanId : undefined,
        isLocked: true
      })

      if (lockState === false) {
        throw new Error('LoanLocked')
      }

      if (isReapply && !isEmpty(prevPayState.result?.newLoanId)) { // Hydrate new loan props in cases where new loan was restored via payment cache and not originally saved to Vuex
        prevPayState.result.newLoanProductId ??= prevPayState.origination.productId
        prevPayState.result.newLoanStoreId ??= prevPayState.origination.storeId
      } else if (!isReapply) {
        try {
          await context.dispatch('cards/getPayDebitCards', null, { root: true }) // This will be needed for guest card handling
        } catch (e) {
          Logger.error('Error getting cards')
          Logger.debug(e)
        }
      }

      context.commit('restoreState', prevPayState)

      const lockResult = await context.dispatch('setAlternatePartyLockout', {
        isLocked: true
      })

      if (lockResult === false) {
        throw new Error('LoanLocked')
      }

      return remotePaymentState.data.route
    } catch (e) {
      Logger.error('Error resuming transaction state')
      Logger.debug(e)
      throw e
    }
  },

  onMakePaymentClicked (context, data) {
    try {
      const { loan, skipValidation } = data
      const { autoPay, loanHeader, loanId, status, storeId } = loan
      const isInOfrStatus = loan.validations ? loan.validations.isInOfrStatus : false
      const hasPayLimitValidations = !isEmpty(loan.validations) && !isEmpty(loan.validations.paymentLimit)
      const showPaymentLimitModal = hasPayLimitValidations && !isEmpty(loan.validations.paymentLimit.payableTitle) && !isEmpty(loan.validations.paymentLimit.payableMessage) && (!loan.validations.paymentLimit.payable || loan.validations.paymentLimit.isWarning)

      if (status === TlxeLoanStatusEnum.Repossessed || status === TlxeLoanStatusEnum.RepossessedChargedOff) {
        router.push({ name: 'OfrVehicleRepossessed', params: { loanId } })
        return
      }

      if (skipValidation !== true && showPaymentLimitModal) {
        context.rootState.session.vueInstance.$root.$bvModal.show(ModalIdentifier.paymentLimit)

        if (loan.validations.paymentLimit.recordEvent) {
          Analytics.logEvent(AnalyticsConstants.eventName.viewItem, { item_id: loan.validations.paymentLimit.payableLimitType, item_name: 'Payment Validation Error', affiliation: loanId.toString(), item_category: 'loan payment validation', item_location_id: 'Account Details' }, { logMarketingEvent: true })
        }

        return
      }

      if (isInOfrStatus) {
        router.push({ name: 'OfrVehicleRecovery', params: { loanHeader, storeId } })
        return
      }

      context.commit('setAutoPay', autoPay)
      router.push({ name: 'MakePayment', params: { loanId } })
    } catch (e) {
      Logger.error('Error handling onMakePaymentClicked')
      Logger.debug(e)
    }
  },

  /**
   * Originates a new loan.
   * @param {Object} context - The Vuex store context object.
   * @remarks Calls an API to queue a Lambda request (so it instantly returns) and polls another API for the response.
   */
  async originateLoan (context) {
    if (isEmpty(context.state.result?.newLoanId)) { // If restoring abandoned application, we may have already created the loan
      await originateLoan(context)
    }

    await context.dispatch('postDisclosureAcknowledgement')
  },

  async postDisclosureAcknowledgement (context, data = {}) {
    try {
      if (context.state.isReapply && context.state.origination.disclosureSubmitted) { // If restoring abandoned application, we may have already submitted disclosures
        Logger.debug('Initiator disclosure already submitted, skipping')
        return
      }

      const paramArray = []

      if (!context.state.isReapply && context.getters.hasAlternate) { // If there is an alternate, both should have acknowledged by now, so get both sets of acknowledgement data
        if (context.getters.isAlternate) { // If you are the alternate, make sure the current borrower info goes in first since they were the last to acknowledge
          paramArray.push.apply(paramArray, data.disclosureData)
          paramArray.push.apply(paramArray, context.state.alternateDiscAcknowledge)
        } else {
          paramArray.push.apply(paramArray, context.state.alternateDiscAcknowledge)
          paramArray.push.apply(paramArray, data.disclosureData)
        }
      } else if (context.state.isReapply) {
        paramArray.push.apply(paramArray, context.state.origination.initiatorDisclosureData)
        data.filteredDisclosures = context.state.origination.initiatorDisclosureDocuments
      } else {
        paramArray.push.apply(paramArray, data.disclosureData)
      }

      const productId = context.state.isReapply ? context.state.origination.productId : context.state.loan.productId
      const storeId = context.state.isReapply ? context.state.origination.storeId : context.state.loan.storeId

      const disclosureType = context.state.isReapply ? DisclosureType.reapply : DisclosureType.refinance
      api.documents.postDisclosureAcknowledgement(context.state.result.newLoanId, context.state.loan.loanHeader, context.rootGetters['session/getCognitoId'], disclosureType, storeId, data.filteredDisclosures, context.getters.isDisbursement, context.state.migrateProduct, productId, context.state.origination.applicationId, ...paramArray).catch(error => { // Not awaiting this call
        Logger.error('Failed to save disclosure acknowledgement')
        Logger.debug(error)
      })

      if (!context.getters.isAlternate) {
        context.commit('saveOriginationInfo', {
          disclosureSubmitted: true
        })
      }

      await context.dispatch('updateRemoteState') // Save latest results and make sure it finishes before navigating off to docusign
    } catch (e) {
      Logger.error('Failed to setup disclosure acknowledgement')
      Logger.debug(e)
    }
  },

  async updateLoanApplicationId (context) {
    const response = await api.origination.updateOrigination(context.state.origination.applicationId)

    if (!isEmpty(response)) {
      context.commit('clearPaymentResult')
      context.commit('saveOriginationInfo', {
        applicationId: response.ApplicationId
      })
    }
  },

  async canRefinance (context, data = {}) {
    try {
      // in case disbursementType isn't set yet in Vuex, send in a possible override
      data = Object.assign(data, { disbursementType: DisbursementTypeEnum.store, isRefi: true }) // 'store', so we don't need all the card information

      const paymentObject = preparePaymentObject(context, data, false)
      const canRefinance = await api.payment.canRefinance(paymentObject)

      Logger.info('Sent payment info successfully to /canRefinance.')
      Logger.debug(canRefinance)

      return canRefinance.canRefi
    } catch (e) {
      Logger.error('Failed to determine refi-ability for loan.')
      Logger.debug(e)
    }
  }
}

const originateLoan = async (context) => {
  let autoPayAlternateMethodId
  const alternateDisburseId = context.rootGetters['customer/activeBank']?.id

  if (context.state.autoPay.method === AutoPayType.bank) {
    autoPayAlternateMethodId = context.rootGetters['cards/defaultPaymentCard']?.cardIds
  } else {
    autoPayAlternateMethodId = context.rootGetters['customer/activeBank']?.id
  }

  const response = await api.origination.postLoanOriginate(context.state.origination.applicationId, alternateDisburseId, autoPayAlternateMethodId, context.state.autoPay.enrolled, context.state.autoPay.method, context.state.autoPay.methodId, context.state.autoPay.methodDisplayNumber, context.state.disbursementAmount, context.state.disburseBankId, context.state.disburseCardIds)

  const messageKey = response.messageKey
  const startTime = moment().unix()
  let currentTime = moment().unix()
  let success

  const timeout = context.rootState.session.loanOriginateTimeout
  let statusResponse

  while ((currentTime - startTime) < timeout) {
    Logger.info('Polling for loan origination status...')
    statusResponse = await api.origination.getLoanOriginateStatus(messageKey).catch(async e => {
      Logger.error('Polling for loan origination status failed. Will likely try again.')
      Logger.debug(e)
    })

    if (!isEmpty(statusResponse)) {
      context.commit('setOriginationResult', {
        ...statusResponse,
        productId: context.state.origination.productId,
        storeId: context.state.origination.storeId
      })

      if (!isEmpty(statusResponse.error)) {
        // quit immediately if we encounter any errors in any of our status checks
        success = false
        break
      }

      if (!isEmpty(statusResponse.loanId)) {
        success = true
        break
      }
    }

    // wait until we try again
    await new Promise(resolve => setTimeout(resolve, 500))

    currentTime = moment().unix()
  }

  await context.dispatch('updateRemoteState') // Save latest results

  // throwing an error will keep the places that call this action behaving as they do before the polling was introduced
  if (success === undefined) {
    throw new Error(`Didn't receive a successful response after checking for ${timeout} seconds.`)
  }

  if (success === false) {
    throw new Error(context.state.result.error ?? 'Failed to originate new loan')
  }
}

/**
 * Prepares the data for submission to either the /payment or /canRefinance API endpoint.
 * @param {Object} context - Vuex action context.
 * @param {Object} data - Vuex action data passed in from caller.
 * @param {Boolean} isPayment - Whether or not this preparation is for a payment (vs. the /canRefinance call).
 * @returns {Object} - The payment object ready to submit to either the /payment or /canRefinance API endpoint.
 */
const preparePaymentObject = (context, data, isPayment) => {
  let username = context.rootState.session.username

  if (phone(username.substring(2))) {
    username = username.substring(2)
  }

  const customerName = context.rootState.customer.customerName
  const conditionalAutoPay = context.state.autoPay
  conditionalAutoPay.enrolled = !isEmpty(data.updateAutoPayStatus) ? data.updateAutoPayStatus : context.state.autoPay?.enrolled // WI324989 - Stop duplicate AP status updates

  let isRefi = false
  let disbursementType = DisbursementTypeEnum.none

  if (data && data.isRefi) {
    isRefi = data.isRefi
  }

  if (!isPayment && !isEmpty(data.disbursementType)) { // only accept a possible disbursementType override during a /canRefinance call
    disbursementType = data.disbursementType
  }

  if (isPayment) {
    context.commit('setPaymentData', { isRefi })
  }

  const paymentObject = {
    email: phone(username) ? undefined : username,
    phoneNumber: phone(username) ? username : undefined,
    loanId: context.state.loan.loanId,
    loanHeader: context.state.loan.loanHeader,
    customerName,
    storePhoneNumber: context.state.storePhoneNumber,
    productBrand: context.state.brand,
    storeId: context.state.loan.storeId,
    customerId: context.rootState.customer.customerId,
    paymentAmount: context.state.paymentAmount,
    paymentCardToken: context.state.paymentCardToken,
    paymentCardIds: context.state.paymentCardIds,
    paymentType: context.state.paymentType,
    disburseCardIds: context.state.disburseCardIds,
    cardHolderName: context.state.cardHolderName,
    cardNumber: context.state.cardNumber,
    isRefi,
    isPartial: context.rootGetters['payment/isPartialPayment'],
    collateralYear: context.state.loan?.collateralDetail?.year,
    collateralMake: context.state.loan?.collateralDetail?.make,
    collateralModel: context.state.loan?.collateralDetail?.model,
    isPayoff: context.state.paymentAmount === context.state.loan.payoffAmount || context.state.paymentType === PaymentTypeEnum.settlement,
    disbursementAmount: context.state.disbursementAmount,
    disbursementType: disbursementType !== DisbursementTypeEnum.none ? disbursementType : context.state.disbursementType,
    lmsId: context.state.loan.lmsId,
    paymentDueDate: context.state.loan.dueDate,
    intentId: context.state.loan.intentId,
    migrateProduct: context.state.migrateProduct, // This means they chose to do a product migration refi
    cvv: context.state.cvv,
    guestPayment: context.state.guestPayment,
    autoPay: conditionalAutoPay,
    riskCheck: context.state.riskCheck,
    ofrPaymentValue: context.state.ofrPaymentValue,
    username,
    discountAmount: context.state.settlementDiscountAmount
  }

  return paymentObject
}
